import { AsyncPipe } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { ChangeDetectionStrategy, Component, DOCUMENT, inject, OnInit } from '@angular/core';
import { NonNullableFormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { ActivatedRoute, Router } from '@angular/router';
import { provideTranslocoScope, TranslocoDirective } from '@jsverse/transloco';
import { appendToRmaDashboardUrl } from '@rma/generic/ng/util';
import { nonNullable } from '@rma/generic/rxjs/operators/non-nullable';
import { rmaShareReplay } from '@rma/generic/rxjs/operators/rma-share-replay';
import { MatErrorOverrideComponent } from '@rma/generic/ui/controls/mat-error';
import { PasswordComponent } from '@rma/generic/ui/controls/password';
import { RmaAuthService } from '@rma/public/accounts/public/orc-authentication';
import { PublicUserContextService } from '@rma/public/accounts/user';
import { CalloutComponent } from '@rma/public/generic/ui/callout';
import { TokenProviderService } from '@rma/public/generic/util/http';
import { catchError, combineLatest, EMPTY, finalize, map, Observable, Subject, take, withLatestFrom } from 'rxjs';
import { AgencyActivationService } from '../data-access-agency-activation/agency-activation.service';
import { AgencyActivationDetails, AgencyActivationErrorType } from '../domain/model/agency-activation.model';

@Component({
  selector: 'rma-agency-activation',
  templateUrl: './agency-activation.component.html',
  styleUrls: ['./agency-activation.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    AsyncPipe,
    CalloutComponent,
    MatButtonModule,
    MatCheckboxModule,
    MatErrorOverrideComponent,
    MatFormFieldModule,
    MatInputModule,
    PasswordComponent,
    ReactiveFormsModule,
    TranslocoDirective,
  ],
  providers: [provideTranslocoScope('agencyactivation')],
})
export class AgencyActivationComponent implements OnInit {
  private readonly activatedRoute = inject(ActivatedRoute);
  private readonly agencyProfileService = inject(AgencyActivationService);
  private readonly authService = inject(RmaAuthService);
  private readonly document = inject<Document>(DOCUMENT);
  private readonly router = inject(Router);
  private readonly tokenService = inject(TokenProviderService);
  private readonly userService = inject(PublicUserContextService);
  private readonly dashboardUrl = inject(appendToRmaDashboardUrl);

  private readonly fb = inject(NonNullableFormBuilder);
  public serverError$: Observable<string>;
  public submitting$: Observable<boolean>;
  public activationDetails$: Observable<AgencyActivationDetails>;

  public errorHandled = false;
  public form = this.fb.group({
    activationCode: ['', Validators.required],
    alias: [''],
    password: [''],
    acceptTermsAndConditions: [false, Validators.requiredTrue],
  });

  private serverErrorSource = new Subject<string>();
  private submittingSource = new Subject<boolean>();

  public constructor() {
    const currentEmail$ = this.userService.currentUser$.pipe(map((user) => user?.email));

    this.serverError$ = this.serverErrorSource.asObservable();
    this.submitting$ = this.submittingSource.asObservable();

    this.activationDetails$ = combineLatest([this.activatedRoute.data, currentEmail$]).pipe(
      map(([{ activation }, currentEmail]) => this.validateActivation(activation, currentEmail)),
      rmaShareReplay(),
    );
  }

  public ngOnInit(): void {
    const activationCode$: Observable<string> = this.activatedRoute.params.pipe(
      map(({ activationCode }) => activationCode),
      nonNullable(),
    );

    this.activationDetails$
      .pipe(
        withLatestFrom(activationCode$),
        map(([{ suggestedAlias, isExistingAccount }, activationCode]) => ({
          activationCode,
          alias: suggestedAlias,
          isExistingAccount,
        })),
        take(1),
      )
      .subscribe((data) => this.setupForm(data));
  }

  public onDeclineInvitation() {
    if (this.validateForm()) {
      this.agencyProfileService
        .declineActivation(this.form.controls.activationCode.value)
        .pipe(
          catchError((e) => this.handleError(e)),
          finalize(() => this.setSubmittingState(false)),
        )
        .subscribe(() => this.router.navigateByUrl('/'));
    }
  }

  public onAcceptInvitation() {
    if (this.validateForm()) {
      this.agencyProfileService
        .verifyActivation(this.form.getRawValue())
        .pipe(
          catchError((e) => this.handleError(e)),
          finalize(() => this.setSubmittingState(false)),
        )
        .subscribe((token) => {
          this.tokenService.setUserToken(token);
          this.document.location = this.dashboardUrl();
        });
    }
  }

  public onLogout() {
    this.authService.logout().subscribe();
    // this is to prevent us having to refresh the page and re-execute the resolver
    this.errorHandled = true;
  }

  private setupForm(details: { activationCode: string; alias: string; isExistingAccount: boolean }) {
    if (!details.isExistingAccount) {
      this.form.controls.alias.addValidators([Validators.required, Validators.minLength(2), Validators.maxLength(25)]);
      this.form.controls.password.addValidators([Validators.required, Validators.minLength(8), Validators.maxLength(100)]);
    } else {
      this.form.controls.alias.clearValidators();
      this.form.controls.password.clearValidators();
    }

    this.form.updateValueAndValidity();

    this.form.patchValue(details);
  }

  private validateForm(): boolean {
    const invalid = this.form.invalid;

    if (invalid) {
      this.form.markAllAsTouched();
    } else {
      this.setSubmittingState(true);
    }

    return !invalid;
  }

  private setSubmittingState(submitting: boolean) {
    this.submittingSource.next(submitting);
  }

  private handleError(error: HttpErrorResponse): Observable<never> {
    // yes this could be translated but the backend messages currently aren't and in theory shouldn't ever fallback this far
    const serverError = error.error?.message ?? error.message ?? 'Failed to verify activation code';
    this.serverErrorSource.next(serverError);

    return EMPTY;
  }

  private validateActivation(details: AgencyActivationDetails, currentEmail: string | undefined): AgencyActivationDetails {
    if (details.activationStatus.name === 'Deleted') {
      return this.appendError(details, 'deleted');
    }

    if (details.activationStatus.name === 'Activated') {
      return this.appendError(details, 'activated');
    }

    if (currentEmail && currentEmail !== details.email) {
      return this.appendError(details, 'loggedIn');
    }

    return details;
  }

  private appendError(details: AgencyActivationDetails, error: AgencyActivationErrorType): AgencyActivationDetails {
    return { ...details, error };
  }
}
